import {
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import moment from "moment";
import { Subscription } from "rxjs";
import { UntypedFormControl } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import {
  SmartFormType,
  SmartFormTypeService,
  TaxReturnType,
  TaxReturnTypeService
} from "../../../../../swaggerServices";
import { DropdownListOption } from "../../../../shared/components/search-dropdown/search-dropdown.component";
import { ConfigStateService } from "../../../config/config-state.service";
import { TenantConfigService } from "../../../../shared/services/tenant-config.service";
import { IFormField } from "../../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { ClientNotificationService } from "../../../client-notification.service";
import { environment } from "../../../../environments/environment";
import { AddUpdateSmartFormTypeComponent } from "./add-update-smart-form-type/add-update-smart-form-type.component";
declare var $: any;

const DISPLAY_DATE_FORMAT = "DD/MM/YYYY HH:mm";

interface IConfigExportSmartFormType extends IFormField {
  selected: boolean;
  displayUpdatedAt?: string;
  displayCreatedAt?: string;
  type: string;
}

interface SmartForm {
  id: number;
}

interface SmartFormIdsByYearId {
  [yearId: string]: number[];
}

@Component({
    selector: "app-smart-form-types",
    templateUrl: "./smart-form-types.component.html",
    styleUrls: ["./smart-form-types.component.scss"],
    standalone: false
})
export class SmartFormTypesComponent implements OnInit, OnDestroy {
  @ViewChild("addUpdateTaxReturnTypeModal")
  addUpdateTaxReturnTypeModal: ElementRef;
  @ViewChild("msgModal")
  msgModal: ElementRef;
  subscriptions: Subscription[] = [];
  smartFormTypes: SmartFormType[];
  filteredSmartFormTypes: SmartFormType[];
  taxYear: UntypedFormControl;
  taxYearOptions: DropdownListOption[];
  smartFormIdsByYearId: SmartFormIdsByYearId;
  creatingBackup: boolean = false;
  configYear: number;
  yearLabel = "Year";

  taxReturnTypes: TaxReturnType[];
  unusedTaxReturnTypes: TaxReturnType[];
  selectedSmartFormType: TaxReturnType = null;

  taxReturnTypeSearch: string;
  uiConfig: TenantConfigService;
  smartFormAlias: string;
  configExportMode = false;
  entireListSelected = false;
  configExportDate = "";
  dateValidationRegex = new RegExp(
    "^\\d{4}\\-(0[1-9]|1[012])\\-(0[1-9]|[12][0-9]|3[01])$"
  );
  smartFormTypesForConfigExport: IConfigExportSmartFormType[];
  selectedSmartFormTypes = [];
  @ViewChild("configExportConfimationModal")
  configExportConfimationModal: ElementRef;
  isProductionMode: boolean;
  @ViewChild("lockSmartFormTypeModal") lockSmartFormTypeModal: ElementRef;
  @ViewChild("unlockSmartFormTypeModal") unlockSmartFormTypeModal: ElementRef;
  lockedSmartFormType: string;

  constructor(
    private typeService: TaxReturnTypeService,
    private config: ConfigStateService,
    private smartFormService: SmartFormTypeService,
    private activatedRoute: ActivatedRoute,
    private clientNotificationService: ClientNotificationService,
    private zone: NgZone,
    private dialog: MatDialog
  ) {
    this.isProductionMode = environment.production;
  }

  ngOnInit() {
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.smartFormAlias = this.uiConfig.config.smartFormAlias;
      }),
      this.smartFormService.getSmartFormTypes().subscribe(result => {
        this.smartFormTypes = result.smartForms;
        this.filteredSmartFormTypes = result.smartForms;
        if (this.taxReturnTypes) {
          this.configureTaxReturnTypes();
        }
        this.loadSmartFormTypeForConfigExport();
        // Moving year extraction into this subscription as other route appeared to be broken
        this.taxYearOptions = this.extractUniqueYears(this.smartFormTypes);
        this.smartFormIdsByYearId = this.extractSmartFormIds(
          this.smartFormTypes
        );
        const taxYr = this.taxYearOptions[this.taxYearOptions.length - 1].value;
        this.configYear = Number(taxYr);
        this.taxYear = new UntypedFormControl(taxYr);
        this.subscriptions.push(
          this.taxYear.valueChanges.subscribe((val: DropdownListOption) => {
            this.configYear = this.taxYear?.value?.value;
            this.filterTaxReturnTypeOnDateChange(this.taxYear?.value?.value);
          })
        );
      }),
      this.typeService.getTaxReturnTypes().subscribe((taxReturnTypes: any) => {
        this.taxReturnTypes = taxReturnTypes;
        if (this.smartFormTypes) {
          this.configureTaxReturnTypes();
        }
      })
    );
  }

  filterTaxReturnTypeOnDateChange(year) {
    if (year == -1) {
      this.filteredSmartFormTypes = this.smartFormTypes;
    } else {
      const smartFormIdsByYear = this.smartFormIdsByYearId[year];
      if (smartFormIdsByYear) {
        this.filteredSmartFormTypes = this.smartFormTypes.filter(sft =>
          smartFormIdsByYear.includes(sft.id)
        );
      }
    }
  }

  extractSmartFormIds(data: SmartFormType[]): SmartFormIdsByYearId {
    const smartFormIdsByYearId: SmartFormIdsByYearId = {};
    const uniqueYears = [...new Set(data.map(smartForm => smartForm.year))];
    for (const year of uniqueYears) {
      const smartFormIds = data
        .filter(smartForm => smartForm.year === year)
        .map(smartForm => smartForm.id);
      smartFormIdsByYearId[year] = smartFormIds;
    }
    return smartFormIdsByYearId;
  }

  extractUniqueYears(data: SmartFormType[]): DropdownListOption[] {
    const years: string[] = [];
    data.forEach(d => {
      if (!years.includes(`${d.year}`)) {
        years.push(`${d.year}`);
      }
    });

    const dropdownOptions: DropdownListOption[] = years.map((year, index) => ({
      id: index,
      name: year,
      value: year,
      order: index + 1,
      selected: false
    }));

    return dropdownOptions;
  }

  configureTaxReturnTypes() {
    this.unusedTaxReturnTypes = [];
    this.taxReturnTypes.forEach(trType => {
      const sfType = this.filteredSmartFormTypes.find(
        sft => sft.taxReturnTypeId === trType.id
      ) as any;

      if (sfType) {
        sfType.taxReturnTypeName = trType.alias;
      } else {
        this.unusedTaxReturnTypes.push(trType);
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  editSmartFormType(taxReturnType) {
    this.openAddUpdateSmartFormTypeDialog(taxReturnType);
  }

  addSmartFormType() {
    this.openAddUpdateSmartFormTypeDialog(null);
  }

  refresh() {
    this.smartFormService.getSmartFormTypes().subscribe(result => {
      this.smartFormTypes = result.smartForms;
      this.filteredSmartFormTypes = result.smartForms;
      this.configureTaxReturnTypes();
      this.smartFormIdsByYearId = this.extractSmartFormIds(this.smartFormTypes);
      this.filterTaxReturnTypeOnDateChange(this.taxYear.value.value);
    });
  }

  toggleConfigExportMode() {
    this.configExportMode = !this.configExportMode;
    if (!this.configExportMode) {
      setTimeout(() =>
        $(".multi-select.dropdown").dropdown({
          placeholder: "All",
          preserveHTML: false
        })
      );
    }
  }

  entireListSelection() {
    this.smartFormTypesForConfigExport.forEach(
      trt => (trt.selected = this.entireListSelected)
    );
  }

  listSelection() {
    this.smartFormTypesForConfigExport.forEach(trt => {
      if (trt.selected === false) return (this.entireListSelected = false);
    });
  }

  loadSmartFormTypeForConfigExport(ids?: string[]) {
    this.smartFormTypesForConfigExport = this.filteredSmartFormTypes
      .map((trt: any) => {
        if (ids && !ids.includes(trt.id)) {
          return;
        }
        return {
          selected: false,
          displayCreatedAt: moment(trt.createdAt)
            .format(DISPLAY_DATE_FORMAT)
            .toString(),
          displayUpdatedAt: moment(trt.updatedAt)
            .format(DISPLAY_DATE_FORMAT)
            .toString(),
          ...trt
        };
      })
      .filter(trt => trt);
  }

  openConfigExportConfimationModal() {
    this.selectedSmartFormTypes = this.smartFormTypesForConfigExport
      .map(trt => {
        if (trt.selected) {
          return { id: trt.id, type: trt, displayUpdatedAt: trt.updatedAt };
        }
      })
      .filter(tt => tt);
    $(this.configExportConfimationModal.nativeElement).modal("show");
    $(this.configExportConfimationModal.nativeElement).scrollTop(
      $(this.configExportConfimationModal.nativeElement)[0].scrollHeight
    );
  }

  clearSelectedSources() {
    this.smartFormTypesForConfigExport.forEach(q => (q.selected = false));
  }

  downloadConfig() {
    if (!this.selectedSmartFormTypes.length) {
      return;
    }
    const smartFormTypeIds: number[] = this.selectedSmartFormTypes.map(
      trt => trt.id
    );
    this.smartFormService
      .downloadSmartFormConfigExport({ smartFormTypeIds })
      .subscribe(
        data => {
          if (!data.fileContent) {
            this.clientNotificationService.appendMessage(
              "File couldn't be fetched, please try again later",
              "Download config",
              false
            );
            return;
          }
          var elem = window.document.createElement("a");
          elem.href =
            "data:application/json," + encodeURIComponent(data.fileContent);
          elem.download = data.fileName ? data.fileName : "package";
          document.body.appendChild(elem);
          elem.click();
          document.body.removeChild(elem);
          this.clearSelectedSources();
          $(this.configExportConfimationModal.nativeElement).modal("hide");
        },
        error => {
          $(this.configExportConfimationModal.nativeElement).modal("hide");
        }
      );
  }

  createBackup() {
    this.creatingBackup = true;
    this.smartFormService
      .backupSmartFormTypeConfig({ year: this.configYear })
      .subscribe(
        () => {
          this.creatingBackup = false;
        },
        error => {
          this.creatingBackup = false;
        }
      );
  }

  lockSmartFormTypeConfirmation(sft) {
    this.lockedSmartFormType = sft.name;
    if (!sft.lock) {
      this.showLockSmartFormTypeModal(sft, () => {
        this.smartFormService.lockSmartFormType({ id: sft.id }).subscribe(
          () => {
            sft.lock = !sft.lock;
            $(this.lockSmartFormTypeModal.nativeElement).modal("hide");
          },
          error => {
            $(this.lockSmartFormTypeModal.nativeElement).modal("hide");
          }
        );
      });
    } else {
      $(this.unlockSmartFormTypeModal.nativeElement).modal("show");
    }
  }

  showLockSmartFormTypeModal(sft, onApprove: () => void) {
    $(this.lockSmartFormTypeModal.nativeElement)
      .modal({
        onApprove: () => {
          this.zone.run(onApprove);
          return false;
        }
      })
      .modal("show");
  }

  private openAddUpdateSmartFormTypeDialog(smartFormType: SmartFormType | null) {
    this.dialog.open(AddUpdateSmartFormTypeComponent, {
      width: '640px',
      data: {
        smartFormType,
        configYear: this.configYear
      },
      autoFocus: 'dialog',
    }).afterClosed().subscribe(updated => {
      if (updated) {
        this.refresh();
      }
    });
  }
}
